/**
 * 기사 화면 조각 — 운영자 기사 화면(/admin/articles)과 광고주 검토 화면(/app/articles)이 함께 쓴다.
 * 링크·영역을 고정하지 않는다(공용 조각이다 — 260917-admin-area-split). 사진 주소의 영역은 부르는 쪽이 정한다.
 */
import type { ArticlePhoto, ArticlePublication, ArticleStatus } from '@geo-admin/shared/contract/api/article.ts';
import { diffWords, hasChanges } from '../lib/diff.ts';
import { cn } from '../lib/utils.ts';
import { Badge } from './ui/card.tsx';

const STATUS: Record<ArticleStatus, { label: string; tone: 'slate' | 'amber' | 'green' | 'blue' }> = {
  draft: { label: '작성 중', tone: 'slate' },
  sent: { label: '광고주 검토 중', tone: 'amber' },
  approved: { label: '승인됨', tone: 'green' },
  published: { label: '게재 완료', tone: 'blue' },
};

export function ArticleStatusBadge({ status }: { status: ArticleStatus }) {
  const s = STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

/**
 * 기사가 지금 어디까지 왔나 — **배지 하나**로 (2026-10-08, 운영자 결정: 사진·언론사 수·광고주 수정은 목록에서 뺀다).
 * 게재 완료 > 배포 대행사 진행상태(기사접수·언론사전달 …) > 우리 상태(광고주 검토 중·승인됨·작성 중).
 */
export function ArticleProgressBadge({ status, pressStatus }: { status: ArticleStatus; pressStatus: string | null }) {
  if (status !== 'published' && pressStatus) {
    return (
      <Badge className="bg-violet-100 text-violet-800" title="배포 대행사(mediap) 진행상태">
        {pressStatus}
      </Badge>
    );
  }
  return <ArticleStatusBadge status={status} />;
}

/**
 * 언론사 게재 목록 — 언론사 · 제목(기사 링크) · 게재일.
 * `onRemove` 를 주면(운영자 화면) 손으로 넣은 게재에 "직접 입력" 표시와 삭제 버튼을 단다.
 */
export function PublicationList({
  publications,
  onRemove,
}: {
  publications: ArticlePublication[];
  onRemove?: (p: ArticlePublication) => void;
}) {
  return (
    <ul className="grid gap-2">
      {publications.map((p) => (
        <li key={p.id} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2 text-slate-500 text-xs">
            <span className="font-semibold text-slate-900">{p.outlet || '언론사'}</span>
            {p.scheduledOn ? <span>{p.scheduledOn.replaceAll('-', '.')} 게재</span> : null}
            {onRemove && p.manual ? (
              <>
                <Badge tone="slate">직접 입력</Badge>
                <button type="button" className="ml-auto text-slate-400 hover:text-red-600" onClick={() => onRemove(p)}>
                  삭제
                </button>
              </>
            ) : null}
          </div>
          <a
            href={p.url}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 block font-medium text-blue-700 hover:underline"
          >
            {p.title}
          </a>
          <span className="block truncate text-slate-400 text-xs">{p.url}</span>
        </li>
      ))}
    </ul>
  );
}

/** 본문 규약(빈 줄 = 문단, `## ` = 소제목)을 그대로 그린다. HTML 을 해석하지 않는다 */
export function ArticlePreview({ title, subtitle, body }: { title: string; subtitle: string; body: string }) {
  const blocks = body
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <article className="space-y-4 leading-7 text-slate-800">
      <h1 className="font-bold text-2xl leading-snug text-slate-900">{title}</h1>
      {subtitle ? <p className="border-slate-200 border-l-4 pl-3 text-slate-600">{subtitle}</p> : null}
      {blocks.map((b, i) =>
        b.startsWith('## ') ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: 미리보기 문단은 순서가 곧 정체성이다
          <h2 key={i} className="pt-2 font-semibold text-lg text-slate-900">
            {b.slice(3)}
          </h2>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: 위와 같다
          <p key={i} className="whitespace-pre-line">
            {b}
          </p>
        ),
      )}
    </article>
  );
}

/** 파일 → base64(앞의 data: 머리 없이) — 기사 사진 올리기에 쓴다 */
export const readBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new Error('파일을 읽지 못했습니다'));
    r.readAsDataURL(file);
  });

/** 사진 파일 주소 — 운영자 화면은 <img> 가 헤더를 못 실어 쿼리로 영역을 알린다(middleware/session.ts) */
export const photoUrl = (articleId: string, photoId: string, realm: 'admin' | 'app') =>
  `/api/articles/${articleId}/photos/${photoId}${realm === 'admin' ? '?realm=admin' : ''}`;

export function PhotoGrid({
  articleId,
  photos,
  realm,
  onDelete,
}: {
  articleId: string;
  photos: ArticlePhoto[];
  realm: 'admin' | 'app';
  onDelete?: (photoId: string) => void;
}) {
  if (photos.length === 0) return null;
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {photos.map((p) => {
        const url = photoUrl(articleId, p.id, realm);
        return (
          <li key={p.id} className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
            <a href={url} target="_blank" rel="noreferrer" download={p.fileName}>
              <img src={url} alt={p.fileName} className="aspect-[4/3] w-full object-contain" />
            </a>
            <div className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-slate-600">
              <span className="flex min-w-0 items-center gap-1">
                {p.origin === 'previous' ? <Badge tone="slate">지난 기사</Badge> : null}
                {p.origin === 'default' ? <Badge tone="slate">기본 사진</Badge> : null}
                <span className="truncate" title={p.fileName}>
                  {p.fileName}
                </span>
              </span>
              {onDelete ? (
                <button type="button" className="shrink-0 text-bad hover:underline" onClick={() => onDelete(p.id)}>
                  삭제
                </button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** 보낸 원고와 지금 원고를 단어 단위로 대조한다 — 지운 말은 빨간 취소선, 넣은 말은 초록 밑줄 */
export function ArticleDiff({ before, after }: { before: string; after: string }) {
  const parts = diffWords(before, after);
  if (!hasChanges(parts)) return <p className="text-sm text-slate-500">바뀐 내용이 없습니다</p>;
  return (
    <p className="whitespace-pre-line text-sm leading-7 text-slate-700">
      {parts.map((p, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: 비교 조각은 순서가 곧 정체성이다
          key={i}
          className={cn(
            p.kind === 'del' && 'bg-red-50 text-red-700 line-through',
            p.kind === 'add' && 'bg-emerald-50 text-emerald-800 underline decoration-emerald-400',
          )}
        >
          {p.text}
        </span>
      ))}
    </p>
  );
}
